import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getDramaById, dramas } from '@/mocks/dramas';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const drama = getDramaById(id || '');

  if (!drama) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">剧集未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的剧集不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: drama.category, href: `/category/${drama.categorySlug}` },
    { label: drama.title },
  ];

  const relatedDramas = dramas
    .filter(d => d.categorySlug === drama.categorySlug && d.id !== drama.id)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${drama.title} - 星空影院`,
    description: drama.description,
    author: {
      '@type': 'Organization',
      name: '星空影院',
    },
    datePublished: `${drama.year}-01-01`,
    publisher: {
      '@type': 'Organization',
      name: '星空影院',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.xingkongyingyuan.com/article/${drama.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify([jsonLd])}</script>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <article className="mt-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full lg:w-[300px]">
                <div className="aspect-[2/3] rounded-lg overflow-hidden sticky top-24">
                  <img
                    src={drama.posterUrl}
                    alt={drama.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-primary-500/20 text-primary-500 text-xs font-semibold rounded-full">
                    {drama.category}
                  </span>
                  <span className="px-3 py-1 bg-accent-500/20 text-accent-500 text-xs font-semibold rounded-full">
                    {drama.rating} 分
                  </span>
                  <span className="text-xs text-foreground-500">{drama.status}</span>
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-2">
                  {drama.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">{drama.titleEn} · {drama.year} · {drama.region}</p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-5 rounded-lg bg-background-100 border border-background-200">
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{drama.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">集数</span>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{drama.episodes}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">状态</span>
                    <p className="text-sm font-medium text-primary-500 mt-1">{drama.status}</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">剧情简介</h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {drama.longDescription}
                  </p>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">主演阵容</h2>
                  <div className="flex flex-wrap gap-2">
                    {drama.cast.map((actor) => (
                      <span
                        key={actor}
                        className="px-3 py-1.5 bg-background-100 border border-background-200 rounded-full text-sm text-foreground-700 whitespace-nowrap"
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">剧集标签</h2>
                  <div className="flex flex-wrap gap-2">
                    {drama.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 bg-secondary-100 text-secondary-900 rounded-full text-xs font-medium whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedDramas.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">
                    同类型推荐
                  </h2>
                  <Link
                    to={`/category/${drama.categorySlug}`}
                    className="text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap flex items-center gap-1"
                  >
                    更多{drama.category} <i className="ri-arrow-right-s-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-5">
                  {relatedDramas.map((d) => (
                    <Link
                      key={d.id}
                      to={`/article/${d.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                        <img
                          src={d.posterUrl}
                          alt={d.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 bg-accent-500 text-white text-xs font-semibold rounded-md">
                            {d.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {d.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{d.episodes}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}